import classNames from 'classnames';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import NumericInput from 'ui-library/inputs/NumericInput';
import {BasicEvent, NumericInputChangeEvent} from 'util/Types';

export interface TableFooterProps {
  canAddRows: boolean;
  rowCountLocked: boolean;
  columnCount: number;
  disableAddingRows: boolean;
  renderAsTableRow: boolean;
  footerControls: JSX.Element | null | undefined;
  showRoundedBottomCorners: boolean;
  maxAddableRows: number;
  numberOfRows: number;

  onDraggerMouseDown: (event: BasicEvent) => void;
  onDraggerDoubleClick: (event: BasicEvent) => void;
  onAddRows: (numRows: number) => void;
}

const TableFooter = (props: TableFooterProps): JSX.Element | null => {
  const [numRowsToAdd, setNumRowsToAdd] = React.useState<number | null>(1);

  const maxRowsReached = props.maxAddableRows === 0;
  if (props.renderAsTableRow) {
    return (
      <tr key="add-row-filler">
        {props.canAddRows && (
          <td
            onMouseDown={props.onDraggerMouseDown}
            onDoubleClick={props.onDraggerDoubleClick}
            className={classNames(
              'mediocre-tableEditable-axisCell mediocre-tableEditable-addRowDraggerCell',
              'cell-sticky',
              {
                'u-no-mouse-target u-faded': props.disableAddingRows,
              }
            )}
            {...getTestProps({component: 'TableEditable', element: 'rowDraggerCell'})}
          >
            <BenchlingIcon icon="bars" />
          </td>
        )}
        {props.rowCountLocked && (
          <Tooltip text="Table length is locked">
            <td
              className={classNames(
                'mediocre-tableEditable-axisCell mediocre-tableEditable-addRowDraggerCell cell-sticky disabled u-faded'
              )}
            >
              <BenchlingIcon icon="lock" />
            </td>
          </Tooltip>
        )}
        {props.footerControls && (
          <td colSpan={props.columnCount} className="mediocre-tableEditable-footerCell">
            <div className="mediocre-tableEditable-footerControls">{props.footerControls}</div>
          </td>
        )}
      </tr>
    );
  } else {
    const maxRowsReachedTooltipMessage = `${props.numberOfRows} row limit reached`;
    let buttonTooltipMessage = BenchlingConfig.ELN_ENABLE_TABLE_EXPERIMENTAL_HOTKEYS
      ? `Add single row (Shift + Enter)`
      : '';
    let isDisabled = false;
    if (maxRowsReached) {
      buttonTooltipMessage = maxRowsReachedTooltipMessage;
      isDisabled = true;
    } else if (!numRowsToAdd || numRowsToAdd < 1) {
      buttonTooltipMessage = 'Number of rows to add must be a number greater than 0';
      isDisabled = true;
    }

    // Reset the input so it doesn't keep showing the previous number of rows if the table is full
    if (maxRowsReached && numRowsToAdd !== 1) {
      setNumRowsToAdd(1);
    }
    return (
      <div
        className={classNames('mediocre-tableEditable-footer', {
          'table-roundedBottomCorners': props.showRoundedBottomCorners,
        })}
      >
        {props.canAddRows && (
          <>
            <div className="mediocre-tableEditable-footer--section">
              <form
                className="mediocre-tableEditable-footer-addRowForm"
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                <NumericInput
                  dataTestAttrs={{component: 'TableFooter', element: 'addRowsInput'}}
                  value={numRowsToAdd}
                  className="mediocre-tableEditable-addRowInput"
                  min={1}
                  isDisabled={maxRowsReached}
                  validateInteger={true}
                  useNumberInputType={true}
                  selectAllOnClick={true}
                  onChange={(e: NumericInputChangeEvent) => {
                    setNumRowsToAdd(e.target.numericValue);
                  }}
                />
                <Tooltip text={buttonTooltipMessage} isDisabled={buttonTooltipMessage === ''}>
                  <Button
                    dataTestAttrs={{component: 'TableFooter', element: 'addRowsButton'}}
                    size={ButtonSize.MINI}
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    className="u-small-spacing-left"
                    type="submit"
                    disabled={isDisabled}
                    onClick={(e) => {
                      e.preventDefault();
                      if (numRowsToAdd && numRowsToAdd > 0) {
                        props.onAddRows(numRowsToAdd);
                      }
                    }}
                  >
                    Add rows
                  </Button>
                </Tooltip>
              </form>
            </div>
          </>
        )}
        {props.rowCountLocked && (
          <div className="mediocre-tableEditable-footer--section">
            <Tooltip text="Table length is locked">
              <Button
                dataTestAttrs={{component: 'TableFooter', element: 'lockRowLengthButton'}}
                size={ButtonSize.MINI}
                buttonStyle={ButtonStyle.TRANSPARENT}
                icon="lock"
                disabled={true}
              />
            </Tooltip>
          </div>
        )}
        <div
          className="mediocre-tableEditable-footer--section u-mini-spacing-right u-flex-grow"
          style={{paddingLeft: 0}}
        >
          {props.footerControls}
        </div>
        <div
          className="mediocre-tableEditable-footer--section u-gray-darker u-spacing-right"
          data-test-element="rowCount"
        >
          {props.numberOfRows} {props.numberOfRows === 1 ? 'row' : 'rows'}
        </div>
      </div>
    );
  }
};

export default React.memo(TableFooter);
